import { useMutation } from '@wirechunk/ui';
import { noop } from 'lodash-es';
import { useEffect } from 'react';
import useLocalStorageState, { type LocalStorageOptions } from 'use-local-storage-state';
import { RefreshSessionDocument } from './mutations.generated.ts';

// Refresh every 8 hours.
const refreshIntervalMs = 8 * 60 * 60 * 1000;

const lastRefreshDefaultValue = {
  defaultValue: Date.now(),
} as const satisfies LocalStorageOptions<number>;

export const useRefreshSession = (userId: string | undefined): void => {
  const [lastRefreshAttemptAt, setLastRefreshAttemptAt] = useLocalStorageState<number>(
    `last-refresh-attempt-at-${userId ?? ''}`,
    lastRefreshDefaultValue,
  );
  const [refreshSession] = useMutation(RefreshSessionDocument, {
    onError: noop,
  });

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    // Refresh function that schedules the next refresh.
    const scheduleRefresh = () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Attempt refresh only when visible.
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const timeSinceLastRefresh = now - lastRefreshAttemptAt;
        if (timeSinceLastRefresh >= refreshIntervalMs) {
          // Setting state here will trigger this effect and schedule the next refresh.
          setLastRefreshAttemptAt(now);
          void refreshSession();
          return;
        }
        timeoutId = setTimeout(
          scheduleRefresh,
          Math.max(0, refreshIntervalMs - timeSinceLastRefresh),
        );
      }
    };

    // Check immediately on mount and schedule next refresh.
    scheduleRefresh();

    document.addEventListener('visibilitychange', scheduleRefresh);

    return () => {
      document.removeEventListener('visibilitychange', scheduleRefresh);
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [refreshSession, setLastRefreshAttemptAt, userId, lastRefreshAttemptAt]);
};
